import { useState, useRef, type FormEvent, type ChangeEvent } from 'react';
import { Plus, Paperclip, ChevronDown, Inbox, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { isUrl } from '@/utils/search';
import { ITEM_TYPE_ICONS, ITEM_TYPE_LABELS, ITEM_TYPES } from '@/config/categories';
import { useVoiceRecognition } from '@/hooks/useVoiceRecognition';
import type { ItemType } from '@/types';

// ============================================
// QUICK ADD BAR
// ============================================

export function QuickAddBar() {
  const [input, setInput] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { openModal } = useApp();
  const { uploadFile } = useData();
  const { isSupported: isVoiceSupported } = useVoiceRecognition();

  // Handle form submit (Enter key or submit button)
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!input.trim()) return;

    // Detect if input is a URL
    if (isUrl(input)) {
      // Check if it might be a podcast
      const isPodcast = isPodcastUrl(input);

      openModal({
        mode: 'add',
        itemType: isPodcast ? 'podcast' : 'link',
        prefillTags: ['inbox'],
        prefillUrl: input.trim(),
      });
    } else {
      // Text input - default to note
      openModal({
        mode: 'add',
        itemType: 'note',
        prefillTags: ['inbox'],
        prefillTitle: input.trim(),
      });
    }

    setInput('');
  };

  // Handle type selection from dropdown
  const handleTypeSelect = (type: ItemType) => {
    setIsDropdownOpen(false);

    if (type === 'file') {
      fileInputRef.current?.click();
      return;
    }

    openModal({
      mode: 'add',
      itemType: type,
      prefillTags: ['inbox'],
      prefillUrl: isUrl(input) ? input : undefined,
      prefillTitle: !isUrl(input) && input ? input : undefined,
    });

    setInput('');
  };

  // Handle file selection
  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadFile(file);
    } catch (error) {
      console.error('Failed to upload file:', error);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file button click
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)] transition-shadow duration-200 focus-within:shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          {/* Main input */}
          <div className="flex-1 relative">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste a link or add something..."
              className="pr-10 transition-shadow duration-200 focus:shadow-sm"
              data-testid="quick-add-input"
            />
          </div>

          {/* Inbox hint */}
          <div
            className="hidden sm:flex items-center gap-1 text-xs text-[var(--text-tertiary)]"
            title="New items go to your inbox for processing"
          >
            <Inbox className="h-3.5 w-3.5" />
            <span>→ inbox</span>
          </div>

          {/* File upload button */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleFileButtonClick}
            aria-label="Upload file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          {/* Voice note button (hidden if unsupported) */}
          {isVoiceSupported && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                openModal({
                  mode: 'add',
                  itemType: 'note',
                  prefillTags: ['inbox'],
                  autoStartVoice: true,
                });
              }}
              aria-label="Voice note"
              title="Create a voice note"
            >
              <Mic className="h-4 w-4" />
            </Button>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            aria-hidden="true"
          />

          {/* Add button with dropdown */}
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button type="button" data-testid="quick-add-submit">
                <Plus className="h-4 w-4 mr-1" />
                Add
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-48">
              {ITEM_TYPES.map((type) => {
                const Icon = ITEM_TYPE_ICONS[type];
                return (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => handleTypeSelect(type)}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {ITEM_TYPE_LABELS[type]}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </form>
      </div>
    </div>
  );
}

// ============================================
// HELPERS
// ============================================

function isPodcastUrl(url: string): boolean {
  const lower = url.toLowerCase();
  const podcastDomains = [
    'podcasts.apple.com',
    'open.spotify.com/episode',
    'open.spotify.com/show',
    'pocketcasts.com',
    'overcast.fm',
    'castro.fm',
    'podcasts.google.com',
  ];
  return podcastDomains.some((domain) => lower.includes(domain));
}
